// Pure layout: render payload → positioned matchups and connector links.
// No DOM access, so it can be unit tested with `node --test`.

export const MATCHUP_W = 220
export const MATCHUP_H = 68
export const HEAD_H = 20
export const SEAT_H = 24
export const COL_GAP = 56
export const ROW_GAP = 16
export const TITLE_H = 48
// Height of the round-name row, which sits at the bottom of the title strip.
export const LABELS_H = 20
export const BRACKET_GAP = 48
export const PAD = 24
export const SPLIT_AT = 32

function positionMatchups(items) {
  const pos = {}
  const step = MATCHUP_H + ROW_GAP
  const cols = [...new Set(items.map(i => i.col))].sort((a, b) => a - b)
  const byCol = new Map(cols.map(c => [c, items.filter(i => i.col === c)]))
  for (let pass = 0; pass < 4; pass++) {
    for (const c of cols) {
      let prevBottom = null
      for (const it of byCol.get(c)) {
        const f = it.feeders.filter(k => pos[k])
        let y
        if (f.length) y = f.reduce((a, k) => a + pos[k].y, 0) / f.length
        else if (pass === 0) y = prevBottom === null ? 0 : prevBottom + ROW_GAP
        else y = pos[it.key].y
        pos[it.key] = { x: c * (MATCHUP_W + COL_GAP), y }
        prevBottom = y + MATCHUP_H
      }
      const sorted = byCol.get(c).slice().sort((a, b) => pos[a.key].y - pos[b.key].y)
      for (let i = 1; i < sorted.length; i++) {
        const p = pos[sorted[i - 1].key], q = pos[sorted[i].key]
        if (q.y < p.y + step) q.y = p.y + step
      }
    }
  }
  const minY = Math.min(...Object.values(pos).map(p => p.y))
  for (const p of Object.values(pos)) p.y -= minY
  return pos
}

function finish(items, pos, ncols) {
  const height = Math.max(...Object.values(pos).map(p => p.y)) + MATCHUP_H
  const width = ncols * (MATCHUP_W + COL_GAP) - COL_GAP
  const links = []
  const colLabels = {}
  for (const it of items) {
    colLabels[Math.round(pos[it.key].x / (MATCHUP_W + COL_GAP))] = it.round
    for (const side of ['top', 'bottom']) {
      const from = it.matchup.feeders[side]
      if (from && pos[from]) links.push({ from, to: it.key, side, kind: it.matchup[side].source.startsWith('loser') ? 'loser' : 'winner' })
    }
  }
  return { pos, width, height, links, colLabels }
}

function trySplit(items) {
  const byKey = Object.fromEntries(items.map(i => [i.key, i]))
  const fin = items[items.length - 1]
  const tf = fin.matchup.feeders.top, bf = fin.matchup.feeders.bottom
  if (!tf || !bf || !byKey[tf] || !byKey[bf]) return null
  const anc = (k, set) => { if (set.has(k)) return set; set.add(k); for (const f of byKey[k].feeders) anc(f, set); return set }
  const left = anc(tf, new Set()), right = anc(bf, new Set())
  for (const k of left) if (right.has(k)) return null
  if (left.size + right.size !== items.length - 1) return null
  const fc = fin.col
  const pl = positionMatchups(items.filter(i => left.has(i.key)))
  const pr = positionMatchups(items.filter(i => right.has(i.key)))
  const pos = {}
  for (const [k, p] of Object.entries(pl)) pos[k] = { x: p.x, y: p.y }
  for (const [k, p] of Object.entries(pr)) pos[k] = { x: (2 * fc - byKey[k].col) * (MATCHUP_W + COL_GAP), y: p.y }
  pos[fin.key] = { x: fc * (MATCHUP_W + COL_GAP), y: (pos[tf].y + pos[bf].y) / 2 }
  return finish(items, pos, 2 * fc + 1)
}

export function layoutBracket(bracket) {
  const items = []
  const keys = new Set()
  bracket.rounds.forEach((r, ri) => {
    const bo = r.matchups.length && r.matchups.every(s => s.best_of === r.matchups[0].best_of) ? r.matchups[0].best_of : null
    const label = bo > 1 ? `${r.name} · Best of ${bo}` : r.name
    r.matchups.forEach(s => { keys.add(s.key); items.push({ key: s.key, col: ri, matchup: s, round: label }) })
  })
  for (const it of items) it.feeders = ['top', 'bottom'].map(side => it.matchup.feeders[side]).filter(k => k && keys.has(k))
  const wantSplit = bracket.layout === 'split' || (bracket.layout !== 'linear' && bracket.entries >= SPLIT_AT)
  if (wantSplit) { const s = trySplit(items); if (s) return { ...s, split: true } }
  return { ...finish(items, positionMatchups(items), bracket.rounds.length), split: false }
}

export function layoutAll(payload) {
  let y = PAD, width = 0
  const blocks = []
  // A bracket's name only tells brackets apart, so a lone bracket gets just its
  // round names and no title strip.
  const titled = payload.brackets.length > 1
  const headH = titled ? TITLE_H : LABELS_H
  for (const b of payload.brackets) {
    const lb = layoutBracket(b)
    blocks.push({ bracket: b, layout: lb, titled, titleY: y, labelsY: y + headH - LABELS_H, originY: y + headH })
    y += headH + lb.height + BRACKET_GAP
    width = Math.max(width, lb.width)
  }
  return { blocks, width: width + PAD * 2, height: y - BRACKET_GAP + PAD }
}
